import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articleDetail } from '@/mocks/articleData';

const articleImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Chinese%20historical%20drama%20behind%20scenes%20film%20set%20with%20actors%20in%20traditional%20Tang%20dynasty%20costumes%2C%20warm%20golden%20set%20lighting%2C%20cinematic%20production%20atmosphere%2C%20professional%20film%20crew%20working%2C%20rich%20amber%20tones&width=1200&height=600&seq=article-detail-01&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Modern%20elegant%20award%20ceremony%20stage%20with%20golden%20trophy%20spotlight%2C%20warm%20amber%20and%20cream%20lighting%2C%20celebratory%20corporate%20event%20atmosphere%2C%20sophisticated%20prestigious%20mood&width=1200&height=600&seq=article-detail-02&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Chinese%20New%20Year%20festive%20cinema%20entrance%20with%20red%20lanterns%20and%20colorful%20movie%20posters%2C%20warm%20celebratory%20lighting%2C%20excited%20crowd%2C%20festive%20holiday%20atmosphere&width=1200&height=600&seq=article-detail-03&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20colorful%20video%20streaming%20app%20interface%20on%20clean%20minimal%20desk%2C%20warm%20natural%20window%20light%2C%20tech%20product%20photography%2C%20professional%20app%20showcase&width=1200&height=600&seq=article-detail-04&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetail[Number(id)] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/articles" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回资讯列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.douhuavideo.com/' },
          { '@type': 'ListItem', position: 2, name: '热门资讯', item: 'https://www.douhuavideo.com/articles' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.douhuavideo.com/article/${article.id}` },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.date,
        description: article.content[0].substring(0, 150),
        url: `https://www.douhuavideo.com/article/${article.id}`,
        publisher: { '@type': 'Organization', name: '豆花视频' },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to="/articles" className="hover:text-primary-600 transition-colors cursor-pointer">热门资讯</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium line-clamp-1">{article.title}</li>
          </ol>
        </nav>

        {/* Article Header */}
        <header className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount} 阅读
              </span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {articleImages[article.id] && (
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-6">
            <div className="rounded-xl overflow-hidden">
              <img
                src={articleImages[article.id]}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
                title={`豆花视频 - ${article.title}`}
              />
            </div>
          </div>
        )}

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-6 md:py-10 max-w-3xl mx-auto">
          <div className="prose-custom space-y-5">
            {article.content.map((paragraph, index) => (
              <p key={index} className="text-base text-foreground-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Article Footer */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                <span className="px-2.5 py-1 text-xs rounded-md bg-background-100 text-foreground-600">{article.category}</span>
                <span className="px-2.5 py-1 text-xs rounded-md bg-background-100 text-foreground-600">豆花视频</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享：</span>
                <button className="w-8 h-8 rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 flex items-center justify-center transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 flex items-center justify-center transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 flex items-center justify-center transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Back Link */}
          <div className="mt-8">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回资讯列表
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}